'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

interface FAQSectionProps {
  searchQuery?: string;
  className?: string;
}

export default function FAQSection({
  searchQuery = '',
  className = '',
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] =
    useState<string>('All');

  const faqs: FAQ[] = [
    {
      question: 'How do I activate my ParkPing card?',
      answer:
        'To activate your ParkPing card, simply scan the QR code on the card using your smartphone camera. This will take you to the activation page where you can link your phone number. The entire process takes less than 2 minutes.',
      category: 'Getting Started',
    },
    {
      question: 'Is my phone number visible to others?',
      answer:
        'No, your phone number is completely private. When someone scans your card, they can only initiate a call or message through our secure system. Your actual number is never displayed or shared with anyone.',
      category: 'Privacy & Security',
    },
    {
      question: 'What happens if I lose my card?',
      answer:
        'If you lose your card, you can immediately deactivate it from your account dashboard. You can then order a replacement card and transfer your existing number to the new card. Your privacy remains protected throughout this process.',
      category: 'Account Management',
    },
    {
      question: 'Can I use one card for multiple vehicles?',
      answer:
        'Each ParkPing card is designed for one vehicle. However, you can order multiple cards under the same account and manage them all from a single dashboard.',
      category: 'Product Usage',
    },
    {
      question: 'How does the call masking work?',
      answer:
        'When someone scans your card and initiates a call, our system connects both parties without revealing either phone number. The call is routed through our secure servers.',
      category: 'Privacy & Security',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept all major payment methods including credit/debit cards, UPI, net banking, and digital wallets.',
      category: 'Billing & Payments',
    },
    {
      question: 'Do you offer bulk discounts for housing societies?',
      answer:
        'Yes, we offer special pricing for bulk orders. Housing societies and corporate campuses can contact our enterprise team.',
      category: 'Bulk Orders',
    },
    {
      question: 'How long does delivery take?',
      answer:
        'Standard delivery takes 3–5 business days across major Indian cities.',
      category: 'Shipping & Delivery',
    },
    {
      question: 'Can I change my linked phone number?',
      answer:
        'Yes, you can change your linked phone number anytime from your account dashboard.',
      category: 'Account Management',
    },
    {
      question: 'What if the card gets damaged?',
      answer:
        'If your card gets damaged, you can order a replacement at a discounted price.',
      category: 'Product Usage',
    },
    {
      question: 'Is there a subscription fee?',
      answer:
        'No, ParkPing operates on a one-time purchase model. There are no recurring fees.',
      category: 'Billing & Payments',
    },
    {
      question: 'How do I install the card on my vehicle?',
      answer:
        'The card comes with strong adhesive backing and simple installation instructions.',
      category: 'Getting Started',
    },
  ];

  const categories = [
    'All',
    ...Array.from(new Set(faqs.map((faq) => faq.category))),
  ];

  const filteredFAQs = faqs.filter((faq) => {
    const matchesCategory =
      selectedCategory === 'All' ||
      faq.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      id="faqs"
      className={`py-16 bg-white ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">
              Find quick answers to common questions about ParkPing
            </p>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-3 mb-8 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  selectedCategory === category
                    ? 'bg-[#42AF47] text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-sm transition"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  <Icon
                    name="ChevronDownIcon"
                    size={24}
                    className={`text-[#42AF47] transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {openIndex === index && (
                  <div className="px-6 pb-4 text-gray-600">
                    <p className="leading-relaxed">{faq.answer}</p>
                    <span className="inline-block mt-3 text-xs text-[#42AF47] font-medium">
                      {faq.category}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredFAQs.length === 0 && (
            <div className="text-center py-12">
              <Icon
                name="MagnifyingGlassIcon"
                size={48}
                className="text-gray-400 mx-auto mb-4"
              />
              <p className="text-gray-600">
                No FAQs found matching your search. Try different keywords or
                browse all categories.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
